import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormControlLabel,
  Switch,
  CircularProgress,
  Divider,
  Typography,
  InputAdornment
} from '@mui/material';
import WorkingHoursEditor from './WorkingHoursEditor';

const EmployeeFormModal = observer(({ open, onClose, mode, employee, store, defaultWorkingHours }) => {
  // Use the store's formData directly
  const formData = store.formData;

  // No need for useEffect as the store handles this in openModal

  const handleSubmit = async () => {
    try {
      if (mode === 'edit') {
        await store.updateEmployee(employee._id, formData);
      } else {
        await store.createEmployee(formData);
      }
    } catch (err) {
      // Error is handled in the store
    }
  };

  const handleChange = (field) => (event) => {
    store.updateFormField(field, event.target.value);
  };

  const handleServiceChange = (event) => {
    store.updateFormField('services', event.target.value);
  };

  const handleActiveChange = (event) => {
    store.updateFormField('active', event.target.checked);
  };

  const colors = [
    '#FF5722', // Deep Orange
    '#2196F3', // Blue
    '#4CAF50', // Green
    '#9C27B0', // Purple
    '#FF9800', // Orange
    '#00BCD4', // Cyan
    '#E91E63', // Pink
    '#795548', // Brown
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'edit' ? 'Editar Funcionário' : 'Novo Funcionário'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Nome"
            value={formData.name}
            onChange={handleChange('name')}
            fullWidth
            required
          />

          <TextField
            label="Apelido"
            value={formData.nickname}
            onChange={handleChange('nickname')}
            fullWidth
          />

          <TextField
            label="Telefone"
            value={formData.phone}
            onChange={handleChange('phone')}
            fullWidth
            placeholder="11999999999"
          />

          <FormControl fullWidth>
            <InputLabel>Serviços</InputLabel>
            <Select
              multiple
              value={formData.services}
              onChange={handleServiceChange}
              input={<OutlinedInput label="Serviços" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((serviceId) => {
                    const service = store.availableServices.find(s => s.id === serviceId);
                    return (
                      <Chip key={serviceId} label={service?.name || serviceId} size="small" />
                    );
                  })}
                </Box>
              )}
            >
              {store.availableServices.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Per-service price overrides */}
          {formData.services.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Precos por servico (opcional)
              </Typography>
              {formData.services.map((serviceId) => {
                const service = store.availableServices.find(s => s.id === serviceId);
                if (!service) return null;
                const override = formData.servicePrices?.find(sp => sp.serviceId === serviceId);
                const defaultPrice = service.price != null ? parseFloat(service.price).toFixed(2) : '0.00';
                return (
                  <TextField
                    key={serviceId}
                    label={service.name}
                    type="number"
                    size="small"
                    value={override?.price ?? ''}
                    onChange={(e) => store.updateServicePrice(serviceId, e.target.value)}
                    placeholder={`${defaultPrice} (padrao)`}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                    fullWidth
                    sx={{ mb: 1 }}
                  />
                );
              })}
            </Box>
          )}

          <FormControl fullWidth>
            <InputLabel>Cor</InputLabel>
            <Select
              value={formData.color}
              onChange={handleChange('color')}
              label="Cor"
            >
              {colors.map((color) => (
                <MenuItem key={color} value={color}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: color
                      }}
                    />
                    {color}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={handleActiveChange}
              />
            }
            label="Funcionário Ativo"
          />

          {/* Working Hours Section */}
          <Divider sx={{ my: 2 }} />

          <WorkingHoursEditor
            store={store}
            defaultWorkingHours={defaultWorkingHours}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={store.isLoading || !formData.name}
        >
          {store.isLoading ? <CircularProgress size={20} /> : mode === 'edit' ? 'Salvar' : 'Criar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default EmployeeFormModal;